import { RESOURCE_TYPES } from '../constant'

export class SourceLoaderService {
  constructor(gaApp) {
    this.gaApp = gaApp

    this.tasks = []
    this.loadedCount = 0
    this.totalTasks = 0
    this.started = false
    this.parser = new DOMParser()

    this.loadingPromise = null
    this.resolveLoading = null
    this.rejectLoading = null
  }

  _addTask(src, type, handler) {
    this.tasks.push({
      src,
      type,
      handler,
    })
    this.totalTasks++
    return this
  }

  _addTasks(srcArray, type, handler) {
    const results = []
    let loadedCount = 0
    srcArray.forEach((src, index) => {
      this._addTask(src, type, (result) => {
        results[index] = result
        loadedCount++
        if (loadedCount === srcArray.length) {
          handler && handler(results)
        }
      })
    })
    return this
  }

  loadImage(src, handler) {
    return this._addTask(src, RESOURCE_TYPES.IMAGE, handler)
  }

  loadImages(srcArray, handler) {
    return this._addTasks(srcArray, RESOURCE_TYPES.IMAGE, handler)
  }

  loadSound(src, handler) {
    return this._addTask(src, RESOURCE_TYPES.SOUND, handler)
  }

  loadSounds(srcArray, handler) {
    return this._addTasks(srcArray, RESOURCE_TYPES.SOUND, handler)
  }

  loadSVG(src, handler) {
    return this._addTask(src, RESOURCE_TYPES.SVG, handler)
  }

  loadSVGs(srcArray, handler) {
    return this._addTasks(srcArray, RESOURCE_TYPES.SVG, handler)
  }

  loadVideo(src, handler) {
    return this._addTask(src, RESOURCE_TYPES.VIDEO, handler)
  }

  loadVideos(srcArray, handler) {
    return this._addTasks(srcArray, RESOURCE_TYPES.VIDEO, handler)
  }

  loadBinary(src, handler) {
    return this._addTask(src, RESOURCE_TYPES.BINARY, handler)
  }

  loadBinaries(srcArray, handler) {
    return this._addTasks(srcArray, RESOURCE_TYPES.BINARY, handler)
  }

  loadVideoAsBinary(src, handler) {
    return this._addTask(src, RESOURCE_TYPES.VIDEO_AS_BINARY, handler)
  }

  loadVideosAsBinary(srcArray, handler) {
    return this._addTasks(srcArray, RESOURCE_TYPES.VIDEO_AS_BINARY, handler)
  }

  start() {
    if (this.started) {
      return this.loadingPromise
    }

    this.started = true
    this.loadedCount = 0

    this.loadingPromise = new Promise((resolve, reject) => {
      this.resolveLoading = resolve
      this.rejectLoading = reject
    })

    if (this.tasks.length === 0) {
      this.gaApp.eventBus.publish('module/app/source-loader/progress', 1)

      this._completeLoading()

      return this.loadingPromise
    }

    this.tasks.forEach((task) => {
      if (task.started) {
        return
      }
      task.started = true

      if (task.type === RESOURCE_TYPES.IMAGE) {
        const img = new Image()
        img.crossOrigin = 'anonymous'

        img.onload = () => {
          if (task.handler) {
            task.handler(img)
          }
          this._checkProgress()
        }
        img.onerror = () => {
          console.error(`Failed to load image: ${task.src}`)
          this._checkProgress()
        }
        img.src = task.src
      } else if (task.type === RESOURCE_TYPES.SOUND) {
        const audio = new Audio()

        audio.onloadedmetadata = () => {
          setTimeout(() => {
            if (task.handler) {
              task.handler(audio)
            }
            this._checkProgress()
          }, 100)
        }
        audio.onerror = () => {
          console.error(`Failed to load sound: ${task.src}`)
          this._checkProgress()
        }
        audio.src = task.src
      } else if (task.type === RESOURCE_TYPES.SVG) {
        fetch(task.src, {
          cache: 'force-cache',
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to load SVG: ${task.src}`)
            }
            return response.text()
          })
          .then((svgText) => {
            const svgElement = this.parser.parseFromString(
              svgText,
              'image/svg+xml',
            ).documentElement
            if (task.handler) {
              task.handler(svgElement)
            }
            this._checkProgress()
          })
          .catch((error) => {
            console.error(error)
            this._checkProgress()
            this._handleError(error)
          })
      } else if (task.type === RESOURCE_TYPES.VIDEO) {
        const video = document.createElement('video')

        video.oncanplaythrough = () => {
          if (task.handler) {
            task.handler(video)
          }
          this._checkProgress()
        }
        video.onerror = () => {
          console.error(`Failed to load video: ${task.src}`)
          this._checkProgress()
        }
        video.src = task.src
      } else if (task.type === RESOURCE_TYPES.BINARY) {
        fetch(task.src, {
          cache: 'force-cache',
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to load binary: ${task.src}`)
            }
            return response.arrayBuffer()
          })
          .then((binaryData) => {
            if (task.handler) {
              task.handler(binaryData)
            }
            this._checkProgress()
          })
          .catch((error) => {
            console.error(error)
            this._checkProgress()
            this._handleError(error)
          })
      } else if (task.type === RESOURCE_TYPES.VIDEO_AS_BINARY) {
        fetch(task.src, {
          cache: 'force-cache',
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to load binary: ${task.src}`)
            }

            // используем blob для лучшей совместимости с iOS
            return response.blob()
          })
          .then((blob) => {
            const url = URL.createObjectURL(blob)
            const video = document.createElement('video')

            const c = () => {
              video.removeEventListener('canplaythrough', c)
              if (task.handler) {
                task.handler(video)
              }
              this._checkProgress()
            }
            video.addEventListener('canplaythrough', c)

            video.src = url
            video.load()
          })
          .catch((error) => {
            console.error(error)
            this._checkProgress()
            this._handleError(error)
          })
      }
    })

    this.gaApp.eventBus.publish('module/app/source-loader/start')

    return this.loadingPromise
  }

  stop() {
    if (this.started) {
      this.started = false

      this.gaApp.eventBus.publish('module/app/source-loader/stop')
    }
  }

  _completeLoading() {
    this.started = false
    this.tasks = []
    this.totalTasks = 0

    this.gaApp.eventBus.publish('module/app/source-loader/complete')

    if (this.resolveLoading) {
      this.resolveLoading()
      this.resolveLoading = null
      this.rejectLoading = null
    }
  }

  _checkProgress() {
    this.loadedCount++

    this.gaApp.eventBus.publish(
      'module/app/source-loader/progress',
      this.loadedCount / this.totalTasks,
    )

    if (this.loadedCount >= this.totalTasks) {
      this._completeLoading()
    }
  }

  _handleError(error) {
    if (this.rejectLoading) {
      this.rejectLoading(error)
      this.resolveLoading = null
      this.rejectLoading = null
    }
  }

  destroy() {
    if (this.rejectLoading) {
      this.rejectLoading(new Error('SourceLoaderService destroyed'))
    }

    this.tasks.forEach((task) => {
      task.handler = null
    })

    this.tasks = []
    this.loadedCount = 0
    this.totalTasks = 0
    this.parser = null

    this.loadingPromise = null
    this.resolveLoading = null
    this.rejectLoading = null
  }
}
